import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import {
  FreelancerDetailsResource,
  InvoicesMemberResource,
} from '@/types/resources';
import { api } from '@/utils/api';
import { categories, countries, genders } from '@/utils/data/payments';
import { handleFormErrors } from '@/utils/helpers';

export type FormDataT = {
  name?: string;
  date_of_birth?: string;
  gender?: string;
  personal_code?: string;
  personal_email?: string;
  phone_number?: string;
  address: string;
  country: string;
  city: string;
  post_code: string;
  category?: string;
  work_email?: string;
  vat_number?: string;
  activity_code?: string;
  representative?: string;
  company_address?: string;
};

const BlockHeading = styled(Text)`
  grid-column: span 2;
  margin-bottom: -8px;
`;

interface Props {
  freelancer: FreelancerDetailsResource;
  onClose: () => void;
}

const FreelancerPersonalDetailsModal: FC<Props> = ({ onClose, freelancer }) => {
  const queryClient = useQueryClient();

  const alerts = useAlerts();

  const generateDefaultValues = (freelancer: FreelancerDetailsResource) => {
    return {
      name: freelancer?.member.name ?? '',
      address: freelancer?.member.address ?? '',
      post_code: freelancer?.member.post_code ?? '',
      country: freelancer?.member.country ?? '',
      city: freelancer?.member.city ?? '',
      date_of_birth: freelancer?.dateOfBirth ?? '',
      gender: freelancer?.gender ?? '',
      personal_code: freelancer?.personalCode ?? '',
      personal_email: freelancer?.personalEmail ?? '',
      phone_number: freelancer?.phoneNumber ?? '',
      category: freelancer?.category ?? '',
      work_email: freelancer?.workEmail ?? '',
      vat_number: freelancer?.member.vat_number ?? '',
      activity_code: freelancer?.activityCode ?? '',
      representative: freelancer?.member.representative ?? '',
      company_address: freelancer?.member.company_address ?? '',
    };
  };

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: generateDefaultValues(freelancer),
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        await api.put<FormDataT, InvoicesMemberResource>(
          `/v1/payments/freelancers/personal-details/${freelancer.id}`,
          {
            ...data,
          },
        );

        queryClient.invalidateQueries(['invoices_list'], {
          exact: false,
        });

        queryClient.invalidateQueries(['invoices_members'], {
          exact: true,
        });

        queryClient.invalidateQueries(['freelancers_details'], {
          exact: false,
        });

        alerts.success(
          "Freelancer's personal details has been succesfully updated",
        );

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true, false);
      }
    })(e);
  };

  return (
    <Modal stopPropagation={false}>
      <SidePanel heading="Edit personal details" onClose={onClose}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <BlockHeading $type="caption">Personal details</BlockHeading>
              <FormField<FormDataT>
                control={control}
                name="name"
                label="Name Surname"
                placeholder="Add Name"
              />
              <FormField<FormDataT>
                control={control}
                name="date_of_birth"
                label="Date of Birth"
                placeholder="Add Date"
                type="datepicker"
                datepickerType="date"
              />
              <FormField<FormDataT>
                control={control}
                name="gender"
                label="Gender"
                type="select"
                placeholder="Select gender"
                options={genders}
              />
              <FormField<FormDataT>
                control={control}
                name="personal_code"
                label="Personal Code"
                placeholder="Add Code"
              />
              <FormField<FormDataT>
                control={control}
                name="vat_number"
                label="VAT Number"
                placeholder="Add VAT number"
              />
              <FormField<FormDataT>
                control={control}
                name="activity_code"
                label="Activity Code"
                placeholder="Add activity code"
              />
              <FormField<FormDataT>
                control={control}
                name="representative"
                label="Representative"
                placeholder="Add representative"
              />
              <FormField<FormDataT>
                control={control}
                name="company_address"
                label="Company Address"
                placeholder="Add company address"
              />
              <FormField<FormDataT>
                control={control}
                name="personal_email"
                label="Personal Email"
                placeholder="Add Email"
              />
              <FormField<FormDataT>
                control={control}
                name="phone_number"
                label="Phone Number"
                placeholder="Add Number"
              />
              <FormField<FormDataT>
                control={control}
                name="address"
                label="Address"
                placeholder="Add address"
              />
              <FormField<FormDataT>
                control={control}
                name="country"
                label="Country"
                type="select"
                options={countries}
              />
              <FormField<FormDataT>
                control={control}
                name="city"
                label="City"
                placeholder="Add city"
              />
              <FormField<FormDataT>
                control={control}
                name="post_code"
                label="Postcode"
                placeholder="Add postcode"
              />
              <FormField<FormDataT>
                name="category"
                control={control}
                type="select"
                label="Category"
                options={categories}
              />
              <FormField<FormDataT>
                control={control}
                name="work_email"
                label="Work Email"
                placeholder="Add email"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              Update
            </Button>
            <Button
              color="secondary"
              onClick={(e: MouseEvent) => {
                e.preventDefault();
                onClose();
              }}
            >
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default FreelancerPersonalDetailsModal;
